import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  List,
  Modal,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  CarrierInfoDTO,
  CarrierMappingFormData,
  CreateProducerDTO,
  ProducerFormData
} from '../../types/data/producer-management.type';
import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import React, { useEffect, useState } from 'react';

import { ProducerService } from '../../service/producer-management.service';

const { Option } = Select;
const { Title } = Typography;

interface AddProducerModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const AddProducerModal: React.FC<AddProducerModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm<ProducerFormData>();
  const [loading, setLoading] = useState(false);
  const [availableCarriers, setAvailableCarriers] = useState<CarrierInfoDTO[]>(
    []
  );
  const [carrierMappings, setCarrierMappings] = useState<
    CarrierMappingFormData[]
  >([]);
  const [selectedCarrier, setSelectedCarrier] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');

  const loadAvailableCarriers = async () => {
    try {
      const carriers = await ProducerService.getAvailableCarriers();
      setAvailableCarriers(carriers);
    } catch (error) {
      message.error('Failed to load available carriers');
    }
  };

  const handleAddCarrierMapping = () => {
    if (!selectedCarrier || !agentId.trim()) {
      message.warning('Please select a carrier and enter an agent ID');
      return;
    }

    // Check if carrier already added
    if (
      carrierMappings.some(mapping => mapping.carrierNaic === selectedCarrier)
    ) {
      message.warning('This carrier has already been added');
      return;
    }

    const carrier = availableCarriers.find(c => c.naic === selectedCarrier);
    if (carrier) {
      setCarrierMappings([
        ...carrierMappings,
        {
          carrierNaic: selectedCarrier,
          agentId: agentId.trim()
        }
      ]);
      setSelectedCarrier('');
      setAgentId('');
    }
  };

  const handleRemoveCarrierMapping = (carrierNaic: string) => {
    setCarrierMappings(
      carrierMappings.filter(mapping => mapping.carrierNaic !== carrierNaic)
    );
  };

  const handleSubmit = async (values: ProducerFormData) => {
    setLoading(true);
    try {
      const createData: CreateProducerDTO = {
        name: values.name,
        npnNumber: values.npnNumber,
        isProducerActiveForEApp: values.isProducerActiveForEApp,
        carrierMappings: carrierMappings.map(mapping => ({
          carrierNaic: mapping.carrierNaic,
          agentId: mapping.agentId
        }))
      };

      await ProducerService.createProducer(createData);
      message.success('Producer created successfully');
      form.resetFields();
      setCarrierMappings([]);
      setSelectedCarrier('');
      setAgentId('');
      onSuccess();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to create producer'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setCarrierMappings([]);
    setSelectedCarrier('');
    setAgentId('');
    onCancel();
  };

  const getCarrierName = (naic: string) => {
    const carrier = availableCarriers.find(c => c.naic === naic);
    return carrier ? carrier.name : naic;
  };

  useEffect(() => {
    if (visible) {
      loadAvailableCarriers();
    }
  }, [visible]);

  return (
    <Modal
      title='Add New Producer'
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        initialValues={{
          isProducerActiveForEApp: false
        }}>
        <Form.Item
          label='Producer Name'
          name='name'
          rules={[
            { required: true, message: 'Please enter producer name' },
            { min: 2, message: 'Name must be at least 2 characters' }
          ]}>
          <Input placeholder='Enter producer name' />
        </Form.Item>

        <Form.Item
          label='NPN Number'
          name='npnNumber'
          rules={[
            { required: true, message: 'Please enter NPN number' },
            { pattern: /^[0-9]+$/, message: 'NPN must contain only numbers' }
          ]}>
          <Input placeholder='Enter NPN number' />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              Active for E-App
              <Tooltip title='Active producer will be used for E-App submissions. Only one producer can be active at a time.'>
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          name='isProducerActiveForEApp'
          valuePropName='checked'>
          <Switch />
        </Form.Item>

        <Divider />

        <Title level={5}>Carrier Contracts</Title>

        <Card size='small' style={{ marginBottom: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Select
              placeholder='Select carrier'
              style={{ width: '40%' }}
              value={selectedCarrier}
              onChange={setSelectedCarrier}
              showSearch
              optionFilterProp='children'>
              {availableCarriers
                .filter(
                  carrier =>
                    !carrierMappings.some(
                      mapping => mapping.carrierNaic === carrier.naic
                    )
                )
                .map(carrier => (
                  <Option key={carrier.naic} value={carrier.naic}>
                    {carrier.name} ({carrier.naic})
                  </Option>
                ))}
            </Select>
            <Input
              placeholder='Enter agent ID'
              style={{ width: '40%' }}
              value={agentId}
              onChange={e => setAgentId(e.target.value)}
              onPressEnter={handleAddCarrierMapping}
            />
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={handleAddCarrierMapping}
              style={{ width: '20%' }}>
              Add
            </Button>
          </Space.Compact>
        </Card>

        {carrierMappings.length > 0 && (
          <Card
            title='Carriers Contracted'
            size='small'
            style={{ marginBottom: 16 }}>
            <List
              size='small'
              dataSource={carrierMappings}
              renderItem={mapping => (
                <List.Item
                  actions={[
                    <Button
                      key={mapping.carrierNaic}
                      type='text'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        handleRemoveCarrierMapping(mapping.carrierNaic)
                      }
                    />
                  ]}>
                  <List.Item.Meta
                    title={getCarrierName(mapping.carrierNaic)}
                    description={`NAIC: ${mapping.carrierNaic} | Agent ID: ${mapping.agentId}`}
                  />
                </List.Item>
              )}
            />
          </Card>
        )}

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type='primary' htmlType='submit' loading={loading}>
              Create Producer
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddProducerModal;
